"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  FiAlertCircle,
  FiCalendar,
  FiCheck,
  FiCheckSquare,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiEye,
  FiList,
  FiRefreshCw,
  FiSearch,
  FiSlash,
  FiUser,
  FiX,
  FiXCircle,
} from "react-icons/fi"
import { FaCalendarAlt, FaFileExcel, FaSpinner } from "react-icons/fa"
import { toast } from "react-hot-toast"
import axiosInstance from "../../libs/axios"
import { SelectWithSearch, type SelectOption as ProjectSelectOption } from "../../utils/select-with-search"
import { PaginationType, Pagination as PaginationUtils } from "../../utils/pagination.utils"
import { ExcelDownload } from "../../utils/excel.utils"
import DateRangePicker from "../../components/DateRangePicker"
import { useAuth } from "../../provider/auth.provider"
import { useConfirm } from "../../provider/confirm.provider"
import { useUrlFilters } from "../../hooks/useUrlFilters"

/**
 * Leave > List (and Human Resource > Leave View through `projectScoped`).
 *
 * The table lists the leave applications of everyone below the signed-in user
 * in the reporting hierarchy — never their own (role level 1 and the HR role
 * see all). Approval is two steps: the applicant's reporting person gives the
 * first approval, which sends the leave to that approver's own reporting
 * person for the final one. A leave stays "pending" through both; the Stage
 * column says which step it is on and who it is waiting for.
 *
 * Approving needs leave.approve, rejecting leave.reject. Whether a given row
 * can be decided right now comes from the server (`can_approve` /
 * `can_reject`), which applies the stage rules.
 */

interface PersonRef {
  id: number
  first_name?: string
  last_name?: string
  employee_code?: string | null
}

interface Leave {
  id: number
  user_id: number
  leave_type: string
  leave_start_date: string
  leave_end_date: string
  total_days: number
  leave_reason: string
  leave_description?: string | null
  leave_status: string
  approval_stage?: "level_1" | "level_2"
  over_quota?: boolean
  submitted_at: string
  proof_attachments?: string[]
  approver_remarks?: string | null
  addressed_at?: string | null
  level1_approved_at?: string | null
  level1_remarks?: string | null
  hr_approved_at?: string | null
  hr_remarks?: string | null
  cancel_reason?: string | null
  cancelled_at?: string | null
  updated_at: string
  user?: {
    id: number
    first_name: string
    last_name: string
    email: string
    phone_number?: string | null
    employee_code?: string | null
  }
  // The applicant's reporting person — who the leave goes to first.
  reporting_person?: PersonRef | null
  // Who the leave is waiting on at its current stage.
  decider?: PersonRef | null
  level1_approved_by_user?: PersonRef | null
  level2_decider?: PersonRef | null
  addressed_by_user?: PersonRef | null
  hr_approved_by_user?: PersonRef | null
  can_approve?: boolean
  can_reject?: boolean
}

type StatusTab = "pending" | "approved" | "rejected" | "cancelled" | "all"

interface Filters {
  project_id: string
  leave_type: string
  approval_stage: string
  start_date_from: string
  start_date_to: string
}

const EMPTY_FILTERS: Filters = {
  project_id: "",
  leave_type: "",
  approval_stage: "",
  start_date_from: "",
  start_date_to: "",
}

// The types a leave can be applied / approved as (LeaveType on the server).
const LEAVE_TYPE_OPTIONS = [
  { value: "casual", label: "Casual" },
  { value: "medical", label: "Medical" },
  { value: "accidental", label: "Accidental" },
  { value: "other", label: "Special Approval" },
]

const LEAVE_TYPE_CLASSES: Record<string, string> = {
  casual: "bg-blue-100 text-blue-900",
  medical: "bg-red-100 text-red-700",
  accidental: "bg-amber-100 text-amber-800",
  other: "bg-gray-100 text-gray-700",
}

const STATUS_CLASSES: Record<string, string> = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-700",
  resubmit: "bg-indigo-100 text-indigo-700",
}

const STATUS_TABS: Array<{ key: StatusTab; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "cancelled", label: "Cancelled" },
  { key: "all", label: "All" },
]

const STAGE_LABELS: Record<string, string> = {
  level_1: "Level 1 — Reporting Person",
  level_2: "Level 2 — Final Approval",
}

const leaveTypeLabel = (type?: string | null) =>
  // The retired "adjusted" type reads as Special Approval.
  LEAVE_TYPE_OPTIONS.find((option) => option.value === type)?.label ?? (type ? "Special Approval" : "N/A")

const personName = (person?: { first_name?: string; last_name?: string } | null) =>
  `${person?.first_name ?? ""} ${person?.last_name ?? ""}`.trim()

const formatDate = (value?: string | null) => {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
}

const formatDateTime = (value?: string | null) => {
  if (!value) return "—"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "—"
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const stageOf = (leave: Leave) => leave.approval_stage || "level_1"

/** "Level 2 — Final Approval" while pending; nothing once the leave is decided. */
const stageText = (leave: Leave) => (leave.leave_status === "pending" ? STAGE_LABELS[stageOf(leave)] : "—")

const LeaveTypeBadge: React.FC<{ leave: Leave }> = ({ leave }) => (
  <span className="inline-flex items-center gap-1">
    <span
      className={`px-2 py-0.5 rounded-full text-xs font-medium ${LEAVE_TYPE_CLASSES[leave.leave_type] ?? LEAVE_TYPE_CLASSES.other}`}
    >
      {leaveTypeLabel(leave.leave_type)}
    </span>
    {leave.over_quota && (
      <span
        className="px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
        title="This leave takes the employee past the quota for this leave type"
      >
        Over quota
      </span>
    )}
  </span>
)

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span
    className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_CLASSES[status] ?? "bg-gray-100 text-gray-700"}`}
  >
    {status}
  </span>
)

const LeaveSummary: React.FC<{ leave: Leave }> = ({ leave }) => (
  <div className="mb-4 p-4 bg-gray-50 rounded text-sm space-y-1">
    <div className="flex justify-between">
      <span className="text-gray-500">Employee</span>
      <span className="font-medium text-gray-900">{personName(leave.user) || "N/A"}</span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500">Applied as</span>
      <span className="font-medium text-gray-900">
        {leaveTypeLabel(leave.leave_type)} · {leave.total_days} day{leave.total_days === 1 ? "" : "s"}
      </span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500">Dates</span>
      <span className="font-medium text-gray-900">
        {formatDate(leave.leave_start_date)} – {formatDate(leave.leave_end_date)}
      </span>
    </div>
    <div className="flex justify-between">
      <span className="text-gray-500">Stage</span>
      <span className="font-medium text-gray-900">{STAGE_LABELS[stageOf(leave)]}</span>
    </div>
  </div>
)

// View Leave Details Offcanvas
const ViewLeaveOffcanvas: React.FC<{
  isOpen: boolean
  onClose: () => void
  leave: Leave | null
  onApprove: (leave: Leave) => void
  onReject: (leave: Leave) => void
  onCancel: (leave: Leave) => void
  // Human Resource > Leave View: details only, no actions at all.
  readOnly?: boolean
}> = ({ isOpen, onClose, leave, onApprove, onReject, onCancel, readOnly = false }) => {
  const { hasPermission } = useAuth()
  if (!isOpen || !leave) return null
  const canApprove = hasPermission("leave", "approve") && Boolean(leave.can_approve)
  const canReject = hasPermission("leave", "reject") && Boolean(leave.can_reject)

  const field = (label: string, value: React.ReactNode) => (
    <div>
      <div className="text-sm text-gray-600 mb-1">{label}</div>
      <div className="text-gray-900">{value}</div>
    </div>
  )

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="absolute right-0 top-0 h-full w-full sm:max-w-lg bg-white shadow-xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <h2 className="text-lg font-semibold text-gray-900">Leave Details</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded transition-colors">
            <FiX className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div className="bg-gray-50 rounded p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <FiUser className="w-6 h-6 text-blue-800" />
              </div>
              <div>
                <div className="font-semibold text-gray-900">{personName(leave.user) || "N/A"}</div>
                <div className="text-sm text-gray-600">
                  {[leave.user?.employee_code, leave.user?.email].filter(Boolean).join(" · ")}
                </div>
                {leave.user?.phone_number && <div className="text-sm text-gray-600">{leave.user.phone_number}</div>}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={leave.leave_status} />
              <LeaveTypeBadge leave={leave} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {field("Start Date", <span className="font-medium">{formatDate(leave.leave_start_date)}</span>)}
            {field("End Date", <span className="font-medium">{formatDate(leave.leave_end_date)}</span>)}
          </div>

          <div className="p-3 bg-blue-50 rounded">
            <div className="text-sm text-blue-800 mb-1">Total Duration</div>
            <div className="text-2xl font-bold text-blue-800">
              {leave.total_days} {leave.total_days === 1 ? "Day" : "Days"}
            </div>
          </div>

          {field("Leave Reason", <div className="bg-gray-50 p-3 rounded text-sm">{leave.leave_reason}</div>)}
          {leave.leave_description &&
            field("Description", <div className="bg-gray-50 p-3 rounded text-sm">{leave.leave_description}</div>)}
          {field("Applied On", formatDateTime(leave.submitted_at))}

          <div className="border-t border-gray-200 pt-3 space-y-3">
            <div className="text-sm font-medium text-gray-800">Approval</div>
            {field("Reporting Person", personName(leave.reporting_person) || "No reporting person")}
            {leave.leave_status === "pending" &&
              field(
                "Waiting On",
                <>
                  {personName(leave.decider) || "No reporting person"}
                  <span className="text-xs text-gray-500"> · {STAGE_LABELS[stageOf(leave)]}</span>
                </>
              )}
            {leave.level1_approved_by_user &&
              field(
                "Level 1 Approved By",
                <>
                  {personName(leave.level1_approved_by_user)}
                  <span className="text-xs text-gray-500"> · {formatDateTime(leave.level1_approved_at)}</span>
                  {leave.level1_remarks && (
                    <div className="mt-1 p-2 bg-green-50 rounded text-xs">{leave.level1_remarks}</div>
                  )}
                </>
              )}
            {leave.addressed_by_user &&
              leave.leave_status !== "pending" &&
              field(
                leave.leave_status === "approved" ? "Final Approval By" : "Decided By",
                <>
                  {personName(leave.addressed_by_user)}
                  <span className="text-xs text-gray-500"> · {formatDateTime(leave.addressed_at)}</span>
                  {leave.approver_remarks && (
                    <div
                      className={`mt-1 p-2 rounded text-xs ${leave.leave_status === "approved" ? "bg-green-50" : "bg-red-50"}`}
                    >
                      {leave.approver_remarks}
                    </div>
                  )}
                </>
              )}
            {leave.leave_status === "cancelled" &&
              field(
                "Cancelled",
                <>
                  {formatDateTime(leave.cancelled_at)}
                  {leave.cancel_reason && <div className="mt-1 p-2 bg-gray-50 rounded text-xs">{leave.cancel_reason}</div>}
                </>
              )}
          </div>

          {Array.isArray(leave.proof_attachments) && leave.proof_attachments.length > 0 && (
            <div className="border-t border-gray-200 pt-3">
              <div className="text-sm text-gray-600 mb-2 font-medium">Attachments</div>
              <div className="space-y-2">
                {leave.proof_attachments.map((attachment, idx) => (
                  <a
                    key={idx}
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 p-2 bg-blue-50 rounded hover:bg-blue-100 transition-colors text-sm text-blue-800"
                  >
                    <FiDownload className="w-4 h-4" />
                    <span className="truncate">Attachment {idx + 1}</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {!readOnly && (
            <div className="border-t border-gray-200 pt-4 space-y-2">
              {canApprove && (
                <button
                  onClick={() => onApprove(leave)}
                  className="w-full bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <FiCheck className="w-4 h-4" />
                  Approve Leave
                </button>
              )}
              {canReject && (
                <button
                  onClick={() => onReject(leave)}
                  className="w-full bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <FiXCircle className="w-4 h-4" />
                  Reject Leave
                </button>
              )}
              {(leave.leave_status === "pending" || leave.leave_status === "approved") && (
                <button
                  onClick={() => onCancel(leave)}
                  className="w-full border border-amber-600 text-amber-600 px-4 py-2 rounded hover:bg-amber-50 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                >
                  <FiSlash className="w-4 h-4" />
                  Cancel Leave
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// Approve Leave Modal
const ApproveLeaveModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  leave: Leave | null
  onSuccess: () => void
}> = ({ isOpen, onClose, leave, onSuccess }) => {
  const [remarks, setRemarks] = useState("")
  const [leaveType, setLeaveType] = useState("casual")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  // The approver settles the leave type. It starts on what the leave currently
  // carries (the employee's choice, or the first approver's), and on Casual
  // when that is not one of the known types.
  useEffect(() => {
    if (isOpen && leave) {
      setLeaveType(
        LEAVE_TYPE_OPTIONS.some((option) => option.value === leave.leave_type) ? leave.leave_type : "casual"
      )
      setRemarks("")
      setError("")
    }
  }, [isOpen, leave])

  if (!isOpen || !leave) return null
  const isFinalStep = stageOf(leave) === "level_2"

  const handleApprove = async () => {
    try {
      setLoading(true)
      setError("")
      const response = await axiosInstance.post("/leave/approve-reject", {
        id: leave.id,
        approver_remarks: remarks || undefined,
        leave_status: "approved",
        leave_type: leaveType,
      })
      toast.success(response.data?.message || "Leave approved")
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to approve leave")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

        <div className="relative bg-white rounded shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
              <FiCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Approve Leave</h3>
              <p className="text-sm text-gray-600">
                {isFinalStep ? "Final approval" : "First approval — it then goes to your reporting person"}
              </p>
            </div>
          </div>

          <LeaveSummary leave={leave} />

          {leave.over_quota && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded flex items-start gap-2">
              <FiAlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-orange-800">
                This leave takes the employee past the {leaveTypeLabel(leave.leave_type)} quota.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type *</label>
            <select
              value={leaveType}
              onChange={(e) => setLeaveType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm"
              disabled={loading}
            >
              {LEAVE_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Currently {leaveTypeLabel(leave.leave_type)}. The leave is approved as the type selected here.
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Approval Remarks (Optional)</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              placeholder="Add any comments..."
              maxLength={1000}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50"
            >
              {loading ? "Approving..." : "Approve"}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Reject / Cancel share one reason modal.
const ReasonModal: React.FC<{
  isOpen: boolean
  mode: "reject" | "cancel"
  onClose: () => void
  leave: Leave | null
  onSuccess: () => void
}> = ({ isOpen, mode, onClose, leave, onSuccess }) => {
  const [reason, setReason] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    if (isOpen) {
      setReason("")
      setError("")
    }
  }, [isOpen, leave])

  if (!isOpen || !leave) return null
  const isReject = mode === "reject"

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError(`Please provide a reason for ${isReject ? "rejection" : "cancellation"}`)
      return
    }
    try {
      setLoading(true)
      setError("")
      if (isReject) {
        await axiosInstance.post("/leave/approve-reject", {
          id: leave.id,
          approver_remarks: reason.trim(),
          leave_status: "rejected",
        })
      } else {
        await axiosInstance.post("/leave/cancel", { id: leave.id, cancel_reason: reason.trim() })
      }
      toast.success(isReject ? "Leave rejected" : "Leave cancelled")
      onSuccess()
      onClose()
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isReject ? "reject" : "cancel"} leave`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => !loading && onClose()}></div>

        <div className="relative bg-white rounded shadow-xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`w-12 h-12 rounded-full flex items-center justify-center ${isReject ? "bg-red-100" : "bg-amber-100"}`}
            >
              {isReject ? <FiXCircle className="w-6 h-6 text-red-600" /> : <FiSlash className="w-6 h-6 text-amber-600" />}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{isReject ? "Reject Leave" : "Cancel Leave"}</h3>
              <p className="text-sm text-gray-600">
                {isReject ? "Provide reason for rejection" : "This action cannot be undone"}
              </p>
            </div>
          </div>

          <LeaveSummary leave={leave} />

          {!isReject && leave.leave_status === "approved" && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded flex items-start gap-2">
              <FiAlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                This leave has already been approved. Cancelling it gives the days back to the employee's leave balance.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>
          )}

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isReject ? "Rejection Reason" : "Cancellation Reason"} <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 text-sm resize-none"
              placeholder={`Please provide a reason for ${isReject ? "rejecting" : "cancelling"} this leave...`}
              maxLength={1000}
              disabled={loading}
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading || !reason.trim()}
              className={`flex-1 text-white px-4 py-2 rounded transition-colors text-sm font-medium disabled:opacity-50 ${isReject ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
            >
              {loading ? "Saving..." : isReject ? "Reject" : "Confirm Cancel"}
            </button>
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 border border-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Month grid of the leaves in the current scope.
const CalendarView: React.FC<{
  currentDate: Date
  leaves: Leave[]
  loading: boolean
  onMonthChange: (date: Date) => void
  onOpen: (leave: Leave) => void
}> = ({ currentDate, leaves, loading, onMonthChange, onOpen }) => {
  const year = currentDate.getFullYear()
  const month = currentDate.getMonth()
  const days = useMemo(() => {
    const startingDayOfWeek = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const list: Array<{ date: Date; inMonth: boolean; leaves: Leave[] }> = []
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      list.push({ date: new Date(year, month, -i), inMonth: false, leaves: [] })
    }
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day)
      list.push({
        date,
        inMonth: true,
        leaves: leaves.filter((leave) => {
          const start = new Date(`${leave.leave_start_date}T00:00:00`)
          const end = new Date(`${leave.leave_end_date}T00:00:00`)
          return date >= start && date <= end
        }),
      })
    }
    while (list.length % 7 !== 0) {
      list.push({ date: new Date(year, month + 1, list.length - daysInMonth - startingDayOfWeek + 1), inMonth: false, leaves: [] })
    }
    return list
  }, [year, month, leaves])

  return (
    <div>
      <div className="px-4 sm:px-6 py-3 border-b border-gray-200 flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900">
          {currentDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
          {loading && <FaSpinner className="inline w-4 h-4 ml-2 animate-spin text-blue-700" />}
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={() => onMonthChange(new Date(year, month - 1, 1))} className="p-2 hover:bg-gray-100 rounded" title="Previous month">
            <FiChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button onClick={() => onMonthChange(new Date())} className="px-3 py-1 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">
            Today
          </button>
          <button onClick={() => onMonthChange(new Date(year, month + 1, 1))} className="p-2 hover:bg-gray-100 rounded" title="Next month">
            <FiChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>
      <div className="p-2 sm:p-4">
        <div className="grid grid-cols-7 gap-1 sm:gap-2 mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="text-center text-xs font-medium text-gray-600 py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1 sm:gap-2">
          {days.map((day, index) => {
            const isToday = day.date.toDateString() === new Date().toDateString()
            return (
              <div
                key={index}
                className={`min-h-[60px] sm:min-h-[100px] border rounded p-1 sm:p-2 ${day.inMonth ? "bg-white" : "bg-gray-50"} ${isToday ? "border-blue-500 border-2" : "border-gray-200"}`}
              >
                <span className={`text-xs sm:text-sm font-medium ${day.inMonth ? "text-gray-900" : "text-gray-400"}`}>
                  {day.date.getDate()}
                </span>
                {day.inMonth && day.leaves.length > 0 && (
                  <div className="space-y-1 mt-1">
                    {day.leaves.slice(0, 2).map((leave) => (
                      <button
                        key={leave.id}
                        type="button"
                        onClick={() => onOpen(leave)}
                        title={`${personName(leave.user)} — ${leaveTypeLabel(leave.leave_type)} (${leave.leave_status})`}
                        className={`block w-full text-left text-xs truncate font-medium px-1 py-0.5 rounded hover:opacity-80 ${STATUS_CLASSES[leave.leave_status] ?? "bg-gray-100 text-gray-700"}`}
                      >
                        {leave.user?.first_name}
                      </button>
                    ))}
                    {day.leaves.length > 2 && (
                      <div className="text-xs text-blue-800 font-medium">+{day.leaves.length - 2} more</div>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

// Main Component
//
// `projectScoped` is Human Resource > Leave View: the same list, but of the
// employees assigned to one of the signed-in user's projects (not their
// reporting hierarchy) and strictly read-only — no approve, reject or cancel.
const LeaveManagement: React.FC<{ projectScoped?: boolean }> = ({ projectScoped = false }) => {
  const { user, hasPermission } = useAuth()
  const { confirm, prompt } = useConfirm()
  const canApproveLeaves = !projectScoped && hasPermission("leave", "approve")
  const canRejectLeaves = !projectScoped && hasPermission("leave", "reject")
  const listEndpoint = projectScoped ? "/leave/hr-leaves" : "/leave/all"
  const isUnrestricted = user?.role?.level === 1 || user?.role?.name?.toLowerCase() === "hr"

  const [viewMode, setViewMode] = useState<"list" | "calendar">("list")
  const [currentDate, setCurrentDate] = useState(new Date())
  const [leaves, setLeaves] = useState<Leave[]>([])
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  // Dashboard tiles link here already filtered, e.g. /leaves?leave_status=pending.
  const urlFilters = useUrlFilters({ ...EMPTY_FILTERS, leave_status: "" })
  const [statusTab, setStatusTab] = useState<StatusTab>(
    STATUS_TABS.some((tab) => tab.key === urlFilters.leave_status) ? (urlFilters.leave_status as StatusTab) : "pending"
  )
  const [searchInput, setSearchInput] = useState("")
  const [search, setSearch] = useState("")
  const [filters, setFilters] = useState<Filters>({
    project_id: urlFilters.project_id,
    leave_type: urlFilters.leave_type,
    approval_stage: urlFilters.approval_stage,
    start_date_from: urlFilters.start_date_from,
    start_date_to: urlFilters.start_date_to,
  })
  const [selectedProject, setSelectedProject] = useState<ProjectSelectOption | null>(null)
  const [pagination, setPagination] = useState<PaginationType>({
    page: 1,
    limit: 25,
    total_count: 0,
    total_pages: 0,
    offset: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [bulkBusy, setBulkBusy] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [exportData, setExportData] = useState<any[]>([])

  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null)
  const [viewOpen, setViewOpen] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const [reasonMode, setReasonMode] = useState<"reject" | "cancel" | null>(null)

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 350)
    return () => clearTimeout(handle)
  }, [searchInput])

  const buildParams = useCallback(
    (page: number, limit: number) => {
      const params: Record<string, string | number> = { page, limit }
      if (search) params.search = search
      if (filters.project_id) params.project_id = filters.project_id
      if (filters.leave_type) params.leave_type = filters.leave_type
      if (filters.approval_stage) params.approval_stage = filters.approval_stage

      if (viewMode === "calendar") {
        // The calendar shows the whole month whatever the tab or date filter.
        const pad = (n: number) => String(n).padStart(2, "0")
        const year = currentDate.getFullYear()
        const month = currentDate.getMonth()
        params.start_date_from = `${year}-${pad(month + 1)}-01`
        params.start_date_to = `${year}-${pad(month + 1)}-${pad(new Date(year, month + 1, 0).getDate())}`
      } else {
        if (statusTab !== "all") params.leave_status = statusTab
        if (filters.start_date_from) params.start_date_from = filters.start_date_from
        if (filters.start_date_to) params.start_date_to = filters.start_date_to
        params.sort_by = "submitted_at"
        params.sort_order = "DESC"
      }
      return params
    },
    [search, filters, viewMode, currentDate, statusTab]
  )

  const fetchLeaves = useCallback(
    async (page = 1, limit = pagination.limit) => {
      setLoading(true)
      setError("")
      try {
        const isCalendar = viewMode === "calendar"
        const response = await axiosInstance.get(listEndpoint, {
          params: buildParams(isCalendar ? 1 : page, isCalendar ? 500 : limit),
        })
        const data = response.data?.data || {}
        setLeaves(data.leaves || [])
        if (data.counts) setCounts(data.counts)
        if (!isCalendar && data.pagination) setPagination({ ...data.pagination, limit })
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to load leave applications")
        setLeaves([])
      } finally {
        setLoading(false)
        setSelectedIds([])
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildParams, listEndpoint]
  )

  useEffect(() => {
    fetchLeaves(1, pagination.limit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildParams])

  const refresh = () => fetchLeaves(pagination.page, pagination.limit)

  const selectableLeaves = useMemo(
    () => leaves.filter((leave) => (canApproveLeaves && leave.can_approve) || (canRejectLeaves && leave.can_reject)),
    [leaves, canApproveLeaves, canRejectLeaves]
  )
  const showSelection =
    viewMode === "list" && (canApproveLeaves || canRejectLeaves) && (statusTab === "pending" || statusTab === "all")
  const allSelected =
    selectableLeaves.length > 0 && selectableLeaves.every((leave) => selectedIds.includes(leave.id))
  const activeFilterCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0)
  const totalCount = Object.values(counts).reduce((sum, value) => sum + (Number(value) || 0), 0)

  const setFilter = (key: keyof Filters, value: string) => setFilters((prev) => ({ ...prev, [key]: value }))
  const clearFilters = () => {
    setFilters(EMPTY_FILTERS)
    setSelectedProject(null)
    setSearchInput("")
  }
  const toggleRow = (id: number) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]))
  const toggleAll = () => setSelectedIds(allSelected ? [] : selectableLeaves.map((leave) => leave.id))

  const runBulk = async (action: "approved" | "rejected") => {
    if (selectedIds.length === 0) return
    const count = selectedIds.length
    let remarks: string | null | undefined
    if (action === "rejected") {
      remarks = await prompt({
        title: `Reject ${count} leave application${count === 1 ? "" : "s"}?`,
        message: "Each employee is told their leave was rejected, with this reason.",
        label: "Reason",
        placeholder: "Why are these being rejected?",
        required: true,
        multiline: true,
        maxLength: 1000,
        confirmText: "Reject",
        cancelText: "Go back",
        variant: "danger",
      })
      if (remarks === null) return
    } else {
      const ok = await confirm({
        title: `Approve ${count} leave application${count === 1 ? "" : "s"}?`,
        message:
          "Each leave is approved as the leave type it currently has. A first approval sends the leave on to your reporting person; a final approval settles it.",
        confirmText: "Approve",
        cancelText: "Go back",
        variant: "info",
      })
      if (!ok) return
    }

    setBulkBusy(true)
    try {
      const response = await axiosInstance.post("/leave/approve-reject/bulk", {
        ids: selectedIds,
        leave_status: action,
        ...(remarks && remarks.trim() ? { approver_remarks: remarks.trim() } : {}),
      })
      const { updated_count = 0, skipped = [] } = response.data?.data || {}
      if (updated_count > 0) toast.success(response.data?.message || "Done")
      if (Array.isArray(skipped) && skipped.length > 0) {
        toast.error(
          `${skipped.length} application${skipped.length === 1 ? " was" : "s were"} skipped: ${skipped[0].reason}`
        )
      }
      await refresh()
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Bulk action failed")
    } finally {
      setBulkBusy(false)
    }
  }

  const mapLeaveForExport = (leave: Leave) => ({
    "E-Code": leave.user?.employee_code || "",
    "Employee Name": personName(leave.user) || "N/A",
    "Employee Email": leave.user?.email || "",
    "Leave Type": leaveTypeLabel(leave.leave_type),
    "Over Quota": leave.over_quota ? "Yes" : "No",
    "Start Date": formatDate(leave.leave_start_date),
    "End Date": formatDate(leave.leave_end_date),
    "Total Days": leave.total_days ?? "",
    "Status": leave.leave_status,
    "Stage": stageText(leave),
    "Reason": leave.leave_reason || "",
    "Description": leave.leave_description || "",
    "Applied On": formatDateTime(leave.submitted_at),
    "Reporting Person": personName(leave.reporting_person),
    "Waiting On": leave.leave_status === "pending" ? personName(leave.decider) : "",
    "Level 1 Approved By": personName(leave.level1_approved_by_user),
    "Level 1 Approved At": formatDateTime(leave.level1_approved_at),
    "Level 1 Remarks": leave.level1_remarks || "",
    "Decided By": leave.leave_status === "pending" ? "" : personName(leave.addressed_by_user),
    "Decided At": leave.leave_status === "pending" ? "" : formatDateTime(leave.addressed_at),
    "Remarks": leave.approver_remarks || "",
    "Cancelled At": formatDateTime(leave.cancelled_at),
    "Cancel Reason": leave.cancel_reason || "",
    "Proof Attachment Links": leave.proof_attachments?.length ? leave.proof_attachments.join("\n") : "",
  })

  const fetchExportData = async () => {
    setIsExporting(true)
    try {
      const all: Leave[] = []
      let page = 1
      let totalPages = 1
      do {
        const response = await axiosInstance.get(listEndpoint, { params: buildParams(page, 100) })
        const data = response.data?.data || {}
        all.push(...(data.leaves || []))
        totalPages = Math.max(1, Number(data.pagination?.total_pages) || 1)
        page += 1
      } while (page <= totalPages)

      if (all.length === 0) {
        toast.error("No leave records found for the current filters")
        return
      }
      setExportData(all.map(mapLeaveForExport))
      setShowExportModal(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to prepare leave export")
    } finally {
      setIsExporting(false)
    }
  }

  const emptyMessage =
    activeFilterCount > 0
      ? "No leave applications match these filters."
      : statusTab === "pending"
        ? "No leave application is waiting for a decision."
        : "No leave applications here yet."

  const openLeave = (leave: Leave) => {
    setSelectedLeave(leave)
    setViewOpen(true)
  }

  const headCell = "px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase whitespace-nowrap"
  const cell = "px-4 py-4 text-sm text-gray-700 whitespace-nowrap"
  const columnCount = 15 + (showSelection ? 1 : 0)

  return (
    <>
      <div className="p-4 space-y-4">
        <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-blue-50 text-blue-800 flex items-center justify-center">
                  <FaCalendarAlt className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900">{projectScoped ? "Leave View" : "Leave List"}</h1>
                  <p className="text-sm text-gray-500">
                    {projectScoped
                      ? "Leave applications of employees assigned to your projects. View only."
                      : isUnrestricted
                        ? "Every leave application. Approval is two steps: reporting person, then the next level."
                        : "Leave applications of everyone who reports to you, directly or through your team. Approval is two steps: reporting person, then the next level."}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 bg-gray-100 rounded p-1">
                  <button
                    onClick={() => setViewMode("list")}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium ${viewMode === "list" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"}`}
                  >
                    <FiList className="w-4 h-4" />
                    List
                  </button>
                  <button
                    onClick={() => setViewMode("calendar")}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium ${viewMode === "calendar" ? "bg-white text-blue-800 shadow-sm" : "text-gray-600"}`}
                  >
                    <FiCalendar className="w-4 h-4" />
                    Calendar
                  </button>
                </div>
                <button
                  onClick={refresh}
                  className="px-3 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 bg-white"
                >
                  <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                  Refresh
                </button>
                <button
                  onClick={fetchExportData}
                  disabled={isExporting}
                  className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2"
                >
                  {isExporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
                  {isExporting ? "Preparing..." : "Export"}
                </button>
              </div>
            </div>
          </div>

          {/* Status tabs */}
          {viewMode === "list" && (
            <div className="px-4 sm:px-6 pt-3 border-b border-gray-200 flex flex-wrap gap-1">
              {STATUS_TABS.map((tab) => {
                const count = tab.key === "all" ? totalCount : counts[tab.key] || 0
                const active = statusTab === tab.key
                return (
                  <button
                    key={tab.key}
                    onClick={() => setStatusTab(tab.key)}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${active ? "border-blue-800 text-blue-800" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                  >
                    {tab.label}
                    <span
                      className={`min-w-5 px-1.5 py-0.5 rounded-full text-xs ${active ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-600"}`}
                    >
                      {count}
                    </span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Filters */}
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 bg-gray-50/30">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 items-end">
              <div className="xl:col-span-2">
                <label className="block text-xs font-medium text-gray-700 mb-1">Employee</label>
                <div className="relative">
                  <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Name, E-Code or email"
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project</label>
                <SelectWithSearch
                  placeholder="Search project..."
                  value={selectedProject}
                  onChange={(opt) => {
                    setSelectedProject(opt)
                    setFilter("project_id", opt ? String(opt.value) : "")
                  }}
                  apiEndpoint="/projects/list"
                  dataKey="projects"
                  labelKey="project_name"
                  valueKey="id"
                  searchParam="search"
                  minSearchLength={0}
                  extraParams={{ assigned_user_ids: user?.id }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Leave Type</label>
                <select
                  value={filters.leave_type}
                  onChange={(e) => setFilter("leave_type", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All types</option>
                  {LEAVE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Approval Stage</label>
                <select
                  value={filters.approval_stage}
                  onChange={(e) => setFilter("approval_stage", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Any stage</option>
                  <option value="level_1">{STAGE_LABELS.level_1}</option>
                  <option value="level_2">{STAGE_LABELS.level_2}</option>
                </select>
              </div>
              {viewMode === "list" && (
                <DateRangePicker
                  label="Leave Dates"
                  from={filters.start_date_from}
                  to={filters.start_date_to}
                  onChange={(from, to) => setFilters((prev) => ({ ...prev, start_date_from: from, start_date_to: to }))}
                />
              )}
            </div>
            {activeFilterCount > 0 && (
              <div className="mt-3 flex items-center gap-2 text-sm">
                <span className="text-gray-600">
                  {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
                </span>
                <button onClick={clearFilters} className="text-blue-700 hover:text-blue-900 underline">
                  Clear all
                </button>
              </div>
            )}
          </div>

          {/* Bulk bar */}
          {showSelection && selectedIds.length > 0 && (
            <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-blue-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 text-sm">
                <FiCheckSquare className="w-5 h-5 text-blue-800" />
                <span className="font-semibold text-blue-900">{selectedIds.length} selected</span>
                <button onClick={() => setSelectedIds([])} className="text-xs text-gray-500 hover:text-gray-700 underline">
                  Clear selection
                </button>
              </div>
              <div className="flex items-center gap-2">
                {canApproveLeaves && (
                  <button
                    onClick={() => runBulk("approved")}
                    disabled={bulkBusy}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                  >
                    <FiCheck className="w-4 h-4" />
                    {bulkBusy ? "Working…" : "Approve Selected"}
                  </button>
                )}
                {canRejectLeaves && (
                  <button
                    onClick={() => runBulk("rejected")}
                    disabled={bulkBusy}
                    className="bg-white border border-red-300 text-red-700 px-4 py-2 rounded hover:bg-red-50 text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                  >
                    <FiX className="w-4 h-4" />
                    Reject Selected
                  </button>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="mx-4 sm:mx-6 mt-4 rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {viewMode === "calendar" ? (
            <CalendarView
              currentDate={currentDate}
              leaves={leaves}
              loading={loading}
              onMonthChange={setCurrentDate}
              onOpen={openLeave}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {showSelection && (
                        <th className={`${headCell} w-10`}>
                          <input
                            type="checkbox"
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            checked={allSelected}
                            disabled={selectableLeaves.length === 0}
                            onChange={toggleAll}
                            title="Select every row you can decide on this page"
                          />
                        </th>
                      )}
                      <th className={headCell}>Actions</th>
                      <th className={headCell}>Employee</th>
                      <th className={headCell}>Leave Type</th>
                      <th className={headCell}>Start Date</th>
                      <th className={headCell}>End Date</th>
                      <th className={headCell}>Days</th>
                      <th className={headCell}>Applied On</th>
                      <th className={headCell}>Stage</th>
                      <th className={headCell}>Waiting On</th>
                      <th className={headCell}>Reason</th>
                      <th className={headCell}>Level 1 Approved By</th>
                      <th className={headCell}>Status</th>
                      <th className={headCell}>Decided By</th>
                      <th className={headCell}>Decided On</th>
                      <th className={headCell}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={columnCount} className="px-4 py-12 text-center text-sm text-gray-600">
                          Loading...
                        </td>
                      </tr>
                    ) : leaves.length === 0 ? (
                      <tr>
                        <td colSpan={columnCount} className="px-4 py-12 text-center text-sm text-gray-500">
                          {emptyMessage}
                        </td>
                      </tr>
                    ) : (
                      leaves.map((leave) => {
                        const mayApprove = canApproveLeaves && Boolean(leave.can_approve)
                        const mayReject = canRejectLeaves && Boolean(leave.can_reject)
                        const isPending = leave.leave_status === "pending"
                        const decided = !isPending && leave.leave_status !== "cancelled"
                        return (
                          <tr
                            key={leave.id}
                            className={`hover:bg-gray-50 ${selectedIds.includes(leave.id) ? "bg-blue-50/60" : ""}`}
                          >
                            {showSelection && (
                              <td className={cell}>
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                                  checked={selectedIds.includes(leave.id)}
                                  disabled={!mayApprove && !mayReject}
                                  onChange={() => toggleRow(leave.id)}
                                  title={mayApprove || mayReject ? "Select" : "You cannot decide this leave"}
                                />
                              </td>
                            )}
                            <td className={cell}>
                              <div className="flex items-center gap-1">
                                <button onClick={() => openLeave(leave)} className="p-2 text-blue-800 hover:bg-blue-50 rounded" title="View details">
                                  <FiEye className="w-4 h-4" />
                                </button>
                                {mayApprove && (
                                  <button
                                    onClick={() => {
                                      setSelectedLeave(leave)
                                      setApproveOpen(true)
                                    }}
                                    className="p-2 text-green-700 hover:bg-green-50 rounded"
                                    title="Approve"
                                  >
                                    <FiCheck className="w-4 h-4" />
                                  </button>
                                )}
                                {mayReject && (
                                  <button
                                    onClick={() => {
                                      setSelectedLeave(leave)
                                      setReasonMode("reject")
                                    }}
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Reject"
                                  >
                                    <FiXCircle className="w-4 h-4" />
                                  </button>
                                )}
                                {!projectScoped && (isPending || leave.leave_status === "approved") && (
                                  <button
                                    onClick={() => {
                                      setSelectedLeave(leave)
                                      setReasonMode("cancel")
                                    }}
                                    className="p-2 text-amber-600 hover:bg-amber-50 rounded"
                                    title="Cancel leave"
                                  >
                                    <FiSlash className="w-4 h-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className={cell}>
                              <div className="font-medium text-gray-900">{personName(leave.user) || "N/A"}</div>
                              <div className="text-xs text-gray-500">{leave.user?.employee_code || leave.user?.email || ""}</div>
                            </td>
                            <td className={cell}>
                              <LeaveTypeBadge leave={leave} />
                            </td>
                            <td className={cell}>{formatDate(leave.leave_start_date)}</td>
                            <td className={cell}>{formatDate(leave.leave_end_date)}</td>
                            <td className={`${cell} font-medium`}>{leave.total_days}</td>
                            <td className={cell}>{formatDateTime(leave.submitted_at)}</td>
                            <td className={cell}>{stageText(leave)}</td>
                            <td className={cell}>
                              {isPending
                                ? personName(leave.decider) || <span className="text-gray-400">No reporting person</span>
                                : "—"}
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate" title={leave.leave_reason || ""}>
                              {leave.leave_reason || "—"}
                            </td>
                            <td className={cell}>{personName(leave.level1_approved_by_user) || "—"}</td>
                            <td className={cell}>
                              <StatusBadge status={leave.leave_status} />
                            </td>
                            <td className={cell}>{decided ? personName(leave.addressed_by_user) || "—" : "—"}</td>
                            <td className={cell}>{decided ? formatDate(leave.addressed_at) : "—"}</td>
                            <td
                              className="px-4 py-4 text-sm text-gray-700 max-w-xs truncate"
                              title={leave.approver_remarks || leave.cancel_reason || leave.level1_remarks || ""}
                            >
                              {leave.approver_remarks || leave.cancel_reason || leave.level1_remarks || "—"}
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {pagination.total_count > 0 && (
                <div className="border-t border-gray-200">
                  <PaginationUtils
                    pagination={pagination}
                    onPageChange={(page) => fetchLeaves(page, pagination.limit)}
                    onLimitChange={(limit) => fetchLeaves(1, limit)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ViewLeaveOffcanvas
        isOpen={viewOpen}
        onClose={() => setViewOpen(false)}
        leave={selectedLeave}
        readOnly={projectScoped}
        onApprove={(leave) => {
          setSelectedLeave(leave)
          setViewOpen(false)
          setApproveOpen(true)
        }}
        onReject={(leave) => {
          setSelectedLeave(leave)
          setViewOpen(false)
          setReasonMode("reject")
        }}
        onCancel={(leave) => {
          setSelectedLeave(leave)
          setViewOpen(false)
          setReasonMode("cancel")
        }}
      />

      <ApproveLeaveModal
        isOpen={approveOpen}
        onClose={() => setApproveOpen(false)}
        leave={selectedLeave}
        onSuccess={refresh}
      />

      <ReasonModal
        isOpen={reasonMode !== null}
        mode={reasonMode ?? "reject"}
        onClose={() => setReasonMode(null)}
        leave={selectedLeave}
        onSuccess={refresh}
      />

      <ExcelDownload
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        data={exportData}
        filename="leave_applications"
        title="Leave Applications Export"
        description="Download the leave applications with the applied filters"
      />
    </>
  )
}

export default LeaveManagement
